import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  signal,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MenuItem, SurveyType } from 'src/app/shared/data/models/menu-item.model';
import { SharedService } from 'src/app/shared/data/shared.service';
import { PillService } from '../data/pill.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '../data/pipes/translate.pipe';
import { Subscription } from 'rxjs';
import { TranslationService } from '../data/translation.service';

interface ResourceItem {
  icon: string;
  title: string;
  description: string;
  link: string;
  isExternalRedirect: boolean;
  isHowdyLink: boolean;
}

enum Direction {
  LeftRight = 'leftRight',
  RightLeft = 'rightLeft'
}

@Component({
  selector: 'app-feature-pill-menu',
  imports: [CommonModule, RouterLink, TranslatePipe],
  templateUrl: './feature-pill-menu.component.html',
  styleUrl: './feature-pill-menu.component.scss',
})
export class FeaturePillMenuComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() pillMini = false;
  @Output() expandedChange = new EventEmitter<boolean>();
  @Output() menuItemChange = new EventEmitter<MenuItem>();

  @ViewChild('pillWrapper') pillWrapper!: ElementRef;
  @ViewChildren('menuItem') menuItems!: QueryList<ElementRef>;
  @ViewChild('menu') menu!: ElementRef;
  @ViewChild('selectedSection') selectedSection!: ElementRef;

  isMobile = false;
  selectedIndex = 0;
  pillExpanded = false;
  isHidden = false;
  menuOptionsSignal = signal<MenuItem[]>([]);
  sections: { title: string; icon: string; resources: ResourceItem[] }[] = [
    {
      title: 'WebApp.v1.Shared.Pill.Section.Mind.Title',
      icon: 'fa-brain',
      resources: [
        {
          icon: 'fa-solid fa-list-ul',
          title: 'WebApp.v1.Shared.Pill.Resource.Mind.Title',
          description: 'WebApp.v1.Shared.Pill.Resource.Mind.Description',
          link: 'tips-and-tricks',
          isExternalRedirect: true,
          isHowdyLink: true
        },
        {
          icon: 'fa-solid fa-mobile-signal-out',
          title: 'WebApp.v1.Shared.Pill.OnDemand.Mind.Title',
          description: 'WebApp.v1.Shared.Pill.OnDemand.Mind.Description',
          link: '/on-demand?type=mind',
          isExternalRedirect: false,
          isHowdyLink: false
        }
      ]
    },
    {
      title: 'WebApp.v1.Shared.Pill.Section.Body.Title',
      icon: 'fa-person',
      resources: [
        {
          icon: 'fa-solid fa-list-ul',
          title: 'WebApp.v1.Shared.Pill.Resource.Body.Title',
          description: 'WebApp.v1.Shared.Pill.Resource.Body.Description',
          link: 'body',
          isExternalRedirect: true,
          isHowdyLink: true
        },
        {
          icon: 'fa-solid fa-mobile-signal-out',
          title: 'WebApp.v1.Shared.Pill.OnDemand.Body.Title',
          description: 'WebApp.v1.Shared.Pill.OnDemand.Body.Description',
          link: '/on-demand?type=body',
          isExternalRedirect: false,
          isHowdyLink: false
        }
      ]
    },
    {
      title: 'WebApp.v1.Shared.Pill.Section.Why.Title',
      icon: 'fa-briefcase',
      resources: [
        {
          icon: 'fa-solid fa-check',
          title: 'WebApp.v1.Shared.Pill.AwarenessByHowdy',
          description: 'WebApp.v1.Shared.Pill.AwarenessByHowdy.Description',
          link: '/explain',
          isExternalRedirect: false,
          isHowdyLink: false
        },
        {
          icon: 'fa-regular fa-comment',
          title: 'WebApp.v1.Shared.Pill.GiveUsFeedback',
          description: 'WebApp.v1.Shared.Pill.GiveUsFeedback.Description',
          link: 'https://hr-on.com/contact-us-howdy-',
          isExternalRedirect: true,
          isHowdyLink: false
        }
      ]
    }
  ];
  companyName = '';
  direction = Direction.LeftRight;
  directions = Direction;

  private routeSubscription?: Subscription;

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < 500;
    this.updateSelectedSection();
    this.calculatePillMinWidth();
  }

  constructor(
    private pillService: PillService,
    private sharedService: SharedService,
    private router: Router,
    private route: ActivatedRoute,
    private translationService: TranslationService
  ) {
    effect(() => {
      if (this.pillService.getMenuItems().length === 0) {
        return;
      }

      this.menuOptionsSignal.set(this.pillService.getMenuItems());
      this.selectedIndex = this.menuOptionsSignal().findIndex(item => item.id === this.pillService.getSelectedMenuItemId());
      this.updateSelectedSection();
      this.setOnDemandText();
      this.companyName = this.sharedService.getCompanyName();

      setTimeout(() => {
        this.slideToSelectedItem();
      }, 100);

      if (this.selectedIndex === this.menuOptionsSignal().length - 1) {
        this.direction = Direction.RightLeft;
      }
    });
  }

  ngOnInit(): void {
    this.isMobile = window.innerWidth < 500;

    this.routeSubscription = this.route.queryParams.subscribe(params => {
      if (params['expandMenu']) {
        if (!this.pillExpanded) {
          this.updatePillTransition('none', '1');
          this.pillExpanded = true;
          this.expandedChange.emit(this.pillExpanded);

          setTimeout(() => {
            this.updatePillTransition('', '');
          }, 100);
        }
      }
    });
  }

  ngAfterViewInit(): void {
    setInterval(() => {
      this.updateSelectedSection();
      this.calculatePillMinWidth();
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  calculatePillMinWidth(): void {
    const firstMenuItem = this.menuItems.toArray()[0]?.nativeElement;

    if (!firstMenuItem) {
      return;
    }

    const numnberOfItems = this.menuItems.length;
    const menuItemsWidth = firstMenuItem.offsetWidth * numnberOfItems;
    const menuContainerWidth = this.menu.nativeElement.offsetWidth;

    if (menuItemsWidth < menuContainerWidth) {
      const pillElement = document.querySelector('app-pill') as HTMLElement;

      if (!pillElement) {
        return;
      }

      pillElement.style.width = window.innerWidth < 1100 ? '' : 'unset';
    }
  }

  onGoToHome(): void {
    this.router.navigate(['/available-surveys']);
  }

  onPillToggle(expanded: boolean): void {
    this.pillExpanded = expanded;
    this.isHidden = true;

    setTimeout(() => {
      this.isHidden = false;
    }, 250);

    if (!this.pillExpanded) {
      this.router.navigate([], {
        queryParams: {
          expandMenu: null
        },
        queryParamsHandling: 'merge'
      });
    } else {
      const pillElement = document.querySelector('app-pill') as HTMLElement;
      pillElement.style.width = '';
    }

    this.expandedChange.emit(this.pillExpanded);
  }

  updatePillTransition(transition: string, opacity: string): void {
    const pillElement = document.querySelector('app-pill') as HTMLElement;

    if (pillElement) {
      pillElement.style.transition = transition;
    }

    if (this.pillWrapper) {
      this.pillWrapper.nativeElement.style.transition = transition;
      this.pillWrapper.nativeElement.style.opacity = opacity;
    }
  }

  onItemSelected(index: number): void {
    this.selectedIndex = index;
    this.updateSelectedSection();
    this.menuItemChange.emit(this.menuOptionsSignal()[this.selectedIndex]);
    this.pillService.setSelectedMenuItemById(this.menuOptionsSignal()[this.selectedIndex].id);
  }

  onResourceItemClick(resource: ResourceItem): void {
    if (resource.isExternalRedirect) {
      const languageCode = this.translationService.getLanguageCode();
      let url = '';

      if (resource.isHowdyLink) {
        url = this.sharedService.localizeExternalHowdyLink(resource.link, languageCode);
      } else {
        url = resource.link + languageCode;
      }

      window.open(url, '_blank');

      this.pillExpanded = false;
      this.expandedChange.emit(this.pillExpanded);

      return;
    }

    const [path, queryString] = resource.link.split('?');
    const queryParams = queryString ? {
      type: queryString.split('=')[1]
    } : {};

    this.router.navigate([path], { queryParams });
    this.pillExpanded = false;
    this.expandedChange.emit(this.pillExpanded);
  }

  onNavigateToStats(): void {
    this.router.navigate(['/stats']);
  }

  onMoveToNext(): void {
    if (this.direction === Direction.RightLeft) {
      this.selectedIndex -= 1;

      if (this.selectedIndex === 0) {
        this.direction = Direction.LeftRight;
      }
    } else {
      this.selectedIndex += 1;

      if (this.selectedIndex === this.menuItems.length - 1) {
        this.direction = Direction.RightLeft;
      }
    }

    this.slideToSelectedItem();
    this.updateSelectedSection();
    this.menuItemChange.emit(this.menuOptionsSignal()[this.selectedIndex]);
  }

  private setOnDemandText(): void {
    const who5survey = this.menuOptionsSignal().find(item => item.type === SurveyType.Who5);
    const bodySurvey = this.menuOptionsSignal().find(item => item.type === SurveyType.Body);

    if (who5survey && who5survey.hasOnDemandCase) {
      this.sections[0].resources[1].title = 'WebApp.v1.Shared.Pill.OnDemand.Mind.CaseInProgress.Title';
      this.sections[0].resources[1].description = 'WebApp.v1.Shared.Pill.OnDemand.Mind.CaseInProgress.Description';
    }

    if (bodySurvey && bodySurvey.hasOnDemandCase) {
      this.sections[1].resources[1].title = 'WebApp.v1.Shared.Pill.OnDemand.Body.CaseInProgress.Title';
      this.sections[1].resources[1].description = 'WebApp.v1.Shared.Pill.OnDemand.Body.CaseInProgress.Description';
    }
  }

  private slideToSelectedItem(): void {
    const selectedItem = this.menuItems?.toArray()[this.selectedIndex]?.nativeElement;
    if (selectedItem) {
      selectedItem.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }

  private updateSelectedSection(): void {
    if (!this.menuItems || this.menuItems.length === 0) {
      return;
    }

    const selectedItem = this.menuItems.toArray()[this.selectedIndex]?.nativeElement;

    if (!selectedItem || !this.selectedSection) {
      return;
    }

    if (!this.selectedSection.nativeElement.style.transition) {
      this.selectedSection.nativeElement.style.transition = 'left 0.4s ease, width 0.4s ease';
    }

    if (this.selectedIndex === 0) {
      this.selectedSection.nativeElement.style.borderRadius = '0 100px 100px 0';
    } else if (this.selectedIndex === this.menuItems.length - 1) {
      this.selectedSection.nativeElement.style.borderRadius = '100px 0 0 100px';
    } else {
      this.selectedSection.nativeElement.style.borderRadius = '100px';
    }

    if (this.isMobile) {
      this.selectedSection.nativeElement.style.borderRadius = '0';
    }

    this.selectedSection.nativeElement.style.left = `${selectedItem.offsetLeft}px`;
    this.selectedSection.nativeElement.style.width = `${selectedItem.offsetWidth}px`;
  }
}
